import { EnrichmentConfig, EnrichmentField, AppRole, FIELDS_BY_ROLE, LookupType } from "../../../../api/types";
import { loadSettings, saveSettings } from "../../../settings/settingsStore";
import { isValidColumn } from "../../../../utils/columnUtils";

function settingsKey(role: AppRole): string {
  return `enrichment-${role}`;
}

/**
 * Load saved enrichment settings for the given role.
 * Validates and filters the data so stale or invalid values don't break the form.
 * Returns null if nothing is saved or the data is unusable.
 */
export function loadEnrichmentSettings(
  role: AppRole
): EnrichmentConfig | null {
  const saved = loadSettings<EnrichmentConfig>(settingsKey(role));
  if (!saved || typeof saved !== "object") return null;

  const availableFields = FIELDS_BY_ROLE[role];

  // Validate primitive fields
  if (typeof saved.startRow !== "number" || saved.startRow < 1) return null;
  if (typeof saved.endRow !== "number" || saved.endRow < saved.startRow) return null;
  if (typeof saved.articleCodeColumn !== "string" || !isValidColumn(saved.articleCodeColumn)) return null;

  // Default lookupType to "ArticleCode" for backward compatibility
  const lookupType: LookupType =
    saved.lookupType === "ArticleCode" || saved.lookupType === "Barcode"
      ? saved.lookupType
      : "ArticleCode";

  if (typeof saved.thumbnailSize !== "number" || saved.thumbnailSize < 1) return null;
  if (typeof saved.fitToCell !== "boolean") return null;
  if (typeof saved.noChangeIfNoResults !== "boolean") return null;

  // Filter selectedFields to only those valid for this role
  if (!Array.isArray(saved.selectedFields)) return null;
  const validFields = saved.selectedFields.filter((f: string) =>
    availableFields.includes(f as EnrichmentField)
  ) as EnrichmentField[];
  if (validFields.length === 0) return null;

  // Validate column mapping — keep only valid entries for available fields
  if (!saved.fieldColumnMapping || typeof saved.fieldColumnMapping !== "object") return null;
  const mapping = saved.fieldColumnMapping as Record<string, string>;
  for (const field of availableFields) {
    if (typeof mapping[field] !== "string" || !isValidColumn(mapping[field])) {
      return null;
    }
  }

  return {
    ...saved,
    selectedFields: validFields,
    lookupType,
  };
}

export function saveEnrichmentSettings(role: AppRole, config: EnrichmentConfig): void {
  saveSettings(settingsKey(role), config);
}
